import React from 'react';
import Button from "components/button";
import UseContext from "hooks/useContext";
import {dateWord} from "helper/helper";
import ContentMotif from "pages/createMission/content.motif";
import {Cadenas, Calendar} from "../../../assets/icons";
import SectionWrapper from "../components/SectionWrapper";
import SummaryField from "components/summaryField/SummaryField";
import MissionTemplateSummary from "pages/missionTemplates/components/MissionTemplateSummary";
import {getTemplateTitle} from "pages/missionTemplates/utils/templateListing";
import {getMissionMotifDetail, getMissionMotifTitle} from "pages/createMission/utils/missionMotifs";
import {formatDayMonthYear} from "utils/date";
import {MISSION_STEP, getMissionStepPage} from "pages/createMission/utils/missionSteps";

const formatLocation = (location) => {
    if (!location) return '-'
    if (typeof location === 'string') return location
    return [location.street, location.postcode, location.city].filter(Boolean).join(', ')
}

// "21 sept. 2026": the month stays lower-case, unlike formatDayMonthYear.
const formatPeriodDate = (date) => {
    const parsed = new Date(date)
    if (Number.isNaN(parsed.getTime())) return ''
    return new Intl.DateTimeFormat('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'Europe/Paris',
    }).format(parsed)
}

const formatPeriod = (dateStart, dateEnd) =>
    dateStart && dateEnd
        ? `${formatPeriodDate(dateStart)} → ${formatPeriodDate(dateEnd)}`
        : ''

const formatInvitations = (users) =>
    Array.isArray(users) ? users.map(({name}) => name).filter(Boolean).join(', ') : ''

const Confirmation = () => {
    const {data, setPage, selectedMissionTemplate, language} = UseContext()
    const usesTemplate = !!selectedMissionTemplate?._id

    if (usesTemplate) {
        const label = (key, defaultValue) =>
            language.getValue(`missionCreation.confirmation.${key}`, defaultValue)
        const none = label('none', 'Aucun')
        const noneFeminine = label('noneFeminine', 'Aucune')
        const editStep = (stepKey) => () => setPage(getMissionStepPage(stepKey))

        return (
            <SectionWrapper
                title={label('title', 'Tout est bon ?')}
                description={label(
                    'description',
                    'Relisez la mission avant de la créer. La fiche de poste ne se modifie pas ici.'
                )}
            >
                <div className={'flex flex-col gap-6'}>
                    <div className={'flex flex-col'}>
                        <SummaryField
                            label={label('motif', 'Motif')}
                            value={getMissionMotifTitle(data.motif?.type, language)}
                            detail={getMissionMotifDetail(data.motif, formatDayMonthYear)}
                            editLabel={label('editMotif', 'Modifier le motif')}
                            onEdit={editStep(MISSION_STEP.MOTIF)}
                        />
                        <SummaryField
                            label={label('period', 'Période de la mission')}
                            value={formatPeriod(data.dateStart, data.dateEnd)}
                            editLabel={label('editPeriod', 'Modifier la période')}
                            onEdit={editStep(MISSION_STEP.PERIOD)}
                        />
                        <SummaryField
                            label={label('invitations', 'Intérimaires invités')}
                            value={formatInvitations(data.users_invite) || none}
                            editLabel={label('editInvitations', 'Modifier les invitations')}
                            onEdit={editStep(MISSION_STEP.INVITATION)}
                        />
                        <SummaryField
                            label={label('tempWorkerInstructions', 'Consignes intérimaire')}
                            value={data.other?.trim() || noneFeminine}
                            editLabel={label('editTempWorkerInstructions', 'Modifier les consignes intérimaire')}
                            onEdit={editStep(MISSION_STEP.DETAILS)}
                        />
                        <SummaryField
                            label={label('jobbizInstructions', 'Consignes Jobbiz')}
                            value={data.other_interim?.trim() || noneFeminine}
                            editLabel={label('editJobbizInstructions', 'Modifier les consignes Jobbiz')}
                            onEdit={editStep(MISSION_STEP.DETAILS)}
                        />
                    </div>
                    {/* Everything the template owns, in the order of its preview panel: read-only here. */}
                    <div className={'rounded-lg bg-gray-100 sh-sm'}>
                        <div className={'flex items-center gap-2 px-4 py-3 ft-sm text-gray-700'}>
                            <span aria-hidden="true">
                                <Cadenas wh={16} color={'#6B7280'}/>
                            </span>
                            <span>
                                {label('template', 'Fiche de poste')} : {getTemplateTitle(selectedMissionTemplate)}
                            </span>
                        </div>
                        {/* The rows sit on their own white card, framed by the grey one. */}
                        <div className={'px-1.5 pb-1.5 bg-gray-100'}>
                            <div className={'bg-white rounded-md px-4'}>
                                <MissionTemplateSummary apiTemplate={selectedMissionTemplate}/>
                            </div>
                        </div>
                    </div>
                </div>
            </SectionWrapper>
        )
    }

    return (
        <SectionWrapper
            title="Confirmation"
            description="Vérifiez les informations de votre mission avant de la créer."
        >
            <div className={'flex flex-col gap-6'}>
                <div className={'flex flex-col gap-4 border border-gray-100 rounded-md p-4'}>
                    <div className={'flex justify-between items-center'}>
                        <span className='ft-xl font-medium text-gray-900'>Description</span>
                        <Button color={'DEFAULT'} onClick={()=>{setPage(1)}} size={'SM'}>Modifier</Button>
                    </div>
                    <div className={'ft-sm text-gray-500'}>Secteur d'activité : <span className={'text-gray-900'}>{data.activity || '-'}</span></div>
                    <div className={'ft-sm text-gray-500'}>Qualification : <span className={'text-gray-900'}>{data.qualification || '-'}</span></div>
                </div>
                <div className={'flex flex-col gap-4 border border-gray-100 rounded-md p-4'}>
                    <div className={'flex justify-between items-center'}>
                        <span className='ft-xl font-medium text-gray-900'>Pré-requis</span>
                        <Button color={'DEFAULT'} onClick={()=>{setPage(2)}} size={'SM'}>Modifier</Button>
                    </div>
                    <div className={'ft-sm text-gray-500'}>Matériel : <span className={'text-gray-900'}>{data.tools.length > 0 ? data.tools.join(', ') : 'Aucun'}</span></div>
                    <div className={'ft-sm text-gray-500'}>Permis : <span className={'text-gray-900'}>{data.licences.length > 0 ? data.licences.join(', ') : 'Aucun'}</span></div>
                    <div className={'ft-sm text-gray-500'}>Langues : <span className={'text-gray-900'}>{data.languages.length > 0 ? data.languages.join(', ') : 'Aucune'}</span></div>
                </div>
                <div className={'flex flex-col gap-4 border border-gray-100 rounded-md p-4'}>
                    <div className={'flex justify-between items-center'}>
                        <span className='ft-xl font-medium text-gray-900'>Motif de recrutement</span>
                        <Button color={'DEFAULT'} onClick={()=>{setPage(3)}} size={'SM'}>Modifier</Button>
                    </div>
                    <ContentMotif/>
                </div>
                <div className={'flex flex-col gap-4 border border-gray-100 rounded-md p-4'}>
                    <div className={'flex justify-between items-center'}>
                        <span className='ft-xl font-medium text-gray-900'>Période</span>
                        <div className="flex gap-2">
                            {/* The period and its schedule belong to the mission, never to the
                                template: with a template they share step 5, without it the dates
                                sit on the address step and the schedule on its own. */}
                            <Button color={'DEFAULT'} onClick={()=>{setPage(4)}} size={'SM'}>Modifier</Button>
                            {data.isScheduleCheck && (
                                <Button color={'SECONDARY'} size={'SM'} onClick={() => setPage(5)}>
                                    <Calendar wh={16} color={'#374151'} />
                                    <span className="ml-1">Planning</span>
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className={'ft-sm text-gray-500'}>Adresse : <span className={'text-gray-900'}>{formatLocation(data.location)}</span></div>
                    <div className={'ft-sm text-gray-500'}>Période : <span className={'text-gray-900'}>{data.dateStart && data.dateEnd ? `${dateWord(data.dateStart)} au ${dateWord(data.dateEnd)}` : '-'}</span></div>
                </div>
                <div className={'flex flex-col gap-4 border border-gray-100 rounded-md p-4'}>
                    <div className={'flex justify-between items-center'}>
                        <span className='ft-xl font-medium text-gray-900'>Responsable</span>
                        <Button color={'DEFAULT'} onClick={()=>{setPage(8)}} size={'SM'}>Modifier</Button>
                    </div>
                    <div className={'ft-sm text-gray-500'}>Nom : <span className={'text-gray-900'}>{data.contact_name || '-'}</span></div>
                    <div className={'ft-sm text-gray-500'}>Fonction : <span className={'text-gray-900'}>{data.contact_poste || '-'}</span></div>
                </div>
                <div className={'flex flex-col gap-4 border border-gray-100 rounded-md p-4'}>
                    <div className={'flex justify-between items-center'}>
                        <span className='ft-xl font-medium text-gray-900'>Rémunération</span>
                        <Button color={'DEFAULT'} onClick={()=>{setPage(6)}} size={'SM'}>Modifier</Button>
                    </div>
                    <div className={'ft-sm text-gray-500'}>Taux horaire : <span className={'text-gray-900'}>{data.salary ? `${data.salary} €/h` : '-'}</span></div>
                </div>
                <div className={'flex flex-col gap-4 border border-gray-100 rounded-md p-4'}>
                    <div className={'flex justify-between items-center'}>
                        <span className='ft-xl font-medium text-gray-900'>Invitations</span>
                        <Button color={'DEFAULT'} onClick={()=>{setPage(7)}} size={'SM'}>Modifier</Button>
                    </div>
                    {data.users_invite && data.users_invite.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                            {data.users_invite.map((tempWorker, index) => (
                                <span key={index} className="px-2 py-1 bg-gray-100 rounded-lg ft-sm text-gray-700">
                                    {tempWorker.name}
                                </span>
                            ))}
                        </div>
                    ) : (
                        <span className="ft-sm text-gray-400">Aucun intérimaire invité</span>
                    )}
                </div>
                <div className={'flex flex-col gap-4 border border-gray-100 rounded-md p-4'}>
                    <div className={'flex justify-between items-center'}>
                        <span className='ft-xl font-medium text-gray-900'>Informations supplémentaires</span>
                        <Button color={'DEFAULT'} onClick={()=>{setPage(9)}} size={'SM'}>Modifier</Button>
                    </div>
                    <div className={'ft-sm text-gray-500'}>Pour le candidat : <span className={'text-gray-900'}>{data.other || 'Aucune information'}</span></div>
                    <div className={'ft-sm text-gray-500'}>Pour l'agence : <span className={'text-gray-900'}>{data.other_interim || 'Aucune information'}</span></div>
                </div>
            </div>
        </SectionWrapper>
    );
};

export default Confirmation;